import CodeMirror, { EditorView } from '@uiw/react-codemirror';

import { useEffect, useState } from 'react';
import { json } from '@codemirror/lang-json';
import { linter } from '@codemirror/lint';
import { AnchorType } from '@components/Flow';
import { Alert, alpha } from '@mui/material';

import { ExpressionContainer } from '@common/Expression';
import { useTheme } from '../../styles';
import {
  getVariableOptions,
  jmespathSupport,
  variableAutocomplete,
  variableHighlighter,
} from '@common/Expression/queryLanguage';

interface Props {
  options: any;
  dataEdges?: AnchorType[];
  value: string;
  setValue: React.Dispatch<string>;
  readOnly?: boolean;
}

export const JSONEditor: React.FC<Props> = ({ value, setValue, options, dataEdges, readOnly }) => {
  const theme = useTheme();
  const [errorMessage, setErrorMessage] = useState<string | null>(null); // State for error message

  const handleChange = (value: string) => {
    setValue(value);
  };

  // Linter function for JSON validation with variable support
  const jsonLinter = linter((view) => {
    const content = view.state.doc.toString().trim();

    // Regex to match a valid variable pattern {{variable}}
    const variableRegex = /^\{\{\s*[\w\W]+\s*\}\}$/;

    // First check if the content is a valid variable
    if (variableRegex.test(content)) {
      setErrorMessage(null); // Clear error if valid variable
      return [];
    }

    // If not a valid variable, proceed with JSON validation
    try {
      JSON.parse(content);
      setErrorMessage(null); // Clear error if valid JSON
      return [];
    } catch (error) {
      if (error instanceof Error) {
        const pos = error.message.match(/at position (\d+)/);
        const position = pos ? parseInt(pos[1], 10) : 0;
        setErrorMessage(
          `Invalid JSON syntax at position ${position}. You must format a valid JSON object, or a single variable such as\n"{{ llm.completion.answer }}".`
        ); // Update error message
        return [
          {
            from: position,
            to: position + 1,
            severity: 'error',
            message: 'Invalid JSON syntax',
          },
        ];
      }
      return [];
    }
  });

  const customTheme = EditorView.theme({
    '&': {
      padding: '6px',
      paddingTop: '7px',
      paddingBottom: '3px',
      borderRadius: '6px',
      minHeight: '40px',
      backgroundColor: `${theme.background.standard} !important`,
    },
    '.cm-content .cm-line': {
      caretColor: `${theme.text.standard} !important`,
    },
    '&:focus .cm-content .cm-line': {
      caretColor: `${theme.text.standard} !important`,
    },
    '.cm-scroller': {
      overflow: 'auto',
      fontFamily: 'Monospace',
      fontSize: '12px',
      color: theme.text.standard, // Text color from theme
      backgroundColor: `${theme.background.standard} !important`,
    },
    '.cm-gutters, .cm-gutterElement, .cm-activeLineGutter': {
      backgroundColor: `${theme.background.standard} !important`,
    },
    '.cm-gutters, .ͼ2': {
      borderRight: `1px solid ${alpha(theme.background.inverse, 0.08)} !important`,
    },
    '.ͼe, .ͼd': {
      color: 'inherit !important',
    },
    '.ͼc': {
      color: `${theme.primary} !important`,
    },
    '&.cm-focused': {
      outline: `2px solid ${theme.primary} !important`,
    },
    '.cm-selectionBackground': {
      backgroundColor: `${alpha(theme.background.inverse, 0.08)} !important`,
    },
    '.cm-activeLine': {
      backgroundColor: `${alpha(theme.background.inverse, 0.08)} !important`,
    },
  });

  // Ensure syntax highlighting updates when changing data anchors
  useEffect(() => {
    setValue(value);
  }, [dataEdges]);

  return (
    <>
      <ExpressionContainer>
        <CodeMirror
          value={value}
          extensions={[
            ...variableHighlighter(options || null),
            customTheme,
            json(),
            jsonLinter,
            jmespathSupport(),
            ...variableAutocomplete(getVariableOptions(options || null)),
          ]}
          onChange={handleChange}
          editable={!readOnly}
        />
      </ExpressionContainer>
      {errorMessage && (
        <Alert severity='error' sx={{ mb: 1, whiteSpace: 'pre-wrap' }}>
          {errorMessage}
        </Alert>
      )}
    </>
  );
};
